import '../sass/project.scss';
import '../css/tailwind.css';

// Fonts & Icons
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/700-italic.css';
import '@fontsource/roboto-slab/400.css';
import '@fontsource/roboto-slab/700.css';
import 'material-icons/iconfont/material-icons.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

// Import all of Bootstrap's JS
import { Alert, Collapse, Tooltip, Popover, Dropdown, Offcanvas, Toast } from 'bootstrap';
window.bootstrap = {
    Alert,
    Collapse,
    Tooltip,
    Popover,
    Dropdown,
    Offcanvas,
    Toast,
};

import htmx from 'htmx.org';
import 'htmx-ext-sse';

window.htmx = htmx;

/**
 * Asynchronously loads thumbnails that might still be generating in the background.
 * Looks for elements with the .async-thumbnail class and data-src/data-srcset attributes.
 */
function loadAsyncThumbnails() {
    const thumbnails = document.querySelectorAll('.async-thumbnail');

    thumbnails.forEach((img) => {
        const actualSrc = img.getAttribute('data-src');
        const actualSrcSet = img.getAttribute('data-srcset');

        if (!actualSrc && !actualSrcSet) return;

        // Create a new image to test if the thumbnail exists
        const testImage = new Image();
        testImage.onload = () => {
            // When image loads successfully, update the src and srcset
            if (actualSrc) img.src = actualSrc;
            if (actualSrcSet) img.srcset = actualSrcSet;
            // Mark as loaded to prevent re-processing if needed
            img.classList.remove('async-thumbnail');
            img.classList.add('thumbnail-loaded');
        };
        testImage.onerror = () => {
            // If error (thumbnail not ready), try again after a delay
            setTimeout(() => {
                const timestamp = new Date().getTime();
                if (actualSrc) {
                    const sep = actualSrc.includes('?') ? '&' : '?';
                    testImage.src = `${actualSrc}${sep}t=${timestamp}`;
                }
            }, 5000); // 5 second backoff
        };

        if (actualSrcSet) testImage.srcset = actualSrcSet;
        if (actualSrc) testImage.src = actualSrc;
    });
}

/**
 * Initializes Bootstrap components like tooltips and popovers.
 * This is designed to be safe to run multiple times.
 */
function initBootstrapComponents() {
    if (typeof window.bootstrap === 'undefined') return;

    // Initialize Tooltips
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map((tooltipTriggerEl) => new window.bootstrap.Tooltip(tooltipTriggerEl));

    // Initialize Popovers
    const popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
    popoverTriggerList.map((popoverTriggerEl) => new window.bootstrap.Popover(popoverTriggerEl));
}

function initDropdowns() {
    if (typeof window.bootstrap === 'undefined' || !window.bootstrap.Dropdown) return;

    const dropdownToggles = document.querySelectorAll('[data-bs-toggle="dropdown"]');
    dropdownToggles.forEach((toggle) => {
        window.bootstrap.Dropdown.getOrCreateInstance(toggle);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    // Initial runs
    loadAsyncThumbnails();
    initBootstrapComponents();
    initDropdowns();

    // Re-run on HTMX swaps to catch new content
    document.body.addEventListener('htmx:afterSwap', () => {
        loadAsyncThumbnails();
        initBootstrapComponents();
        initDropdowns();
    });

    // Dynamic Search Bar Logic
    const searchToggleBtn = document.getElementById('searchToggleBtn');
    const searchInputWrapper = document.getElementById('searchInputWrapper');
    const searchInput = document.getElementById('globalSearchInput');
    const searchDropdown = document.getElementById('search-results-dropdown');

    if (searchToggleBtn && searchInputWrapper && searchInput) {
        searchToggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isExpanded = searchInputWrapper.classList.contains('active');

            if (isExpanded) {
                // Keep the toggle focused on opening/closing search.
                searchInputWrapper.classList.remove('active');
                searchToggleBtn.setAttribute('aria-expanded', 'false');
            } else {
                // Open
                searchInputWrapper.classList.add('active');
                searchToggleBtn.setAttribute('aria-expanded', 'true');
                // Focus after transition
                setTimeout(() => searchInput.focus(), 100);
            }
        });

        // Close on click outside, but do not interfere with Bootstrap dropdowns
        document.addEventListener('click', (event) => {
            if (event.target.closest('.dropdown, .dropdown-menu')) {
                return;
            }

            if (!searchInputWrapper.contains(event.target) && !searchToggleBtn.contains(event.target)) {
                // Only close if it's currently open
                if (searchInputWrapper.classList.contains('active')) {
                    searchInputWrapper.classList.remove('active');
                    searchToggleBtn.setAttribute('aria-expanded', 'false');
                }
            }
        });
    }

    // Search Autocomplete Keyboard Navigation
    // Enables arrow key navigation, Enter to select, and Escape to close
    if (searchInput && searchDropdown) {
        let currentIndex = -1;
        let items = [];

        function updateItems() {
            items = Array.from(searchDropdown.querySelectorAll('[role="option"], [data-search-all-results="true"]'));
        }

        function clearSelection() {
            items.forEach((item) => {
                item.removeAttribute('aria-selected');
            });
            currentIndex = -1;
        }

        function setSelection(index) {
            if (index < 0 || index >= items.length) return;

            clearSelection();
            currentIndex = index;
            const selectedItem = items[currentIndex];
            selectedItem.setAttribute('aria-selected', 'true');
            selectedItem.focus();

            // Scroll dropdown to show selected item
            selectedItem.scrollIntoView({ block: 'nearest' });
        }

        function handleSearchInputKeydown(e) {
            updateItems();

            if (items.length === 0) {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    searchDropdown.innerHTML = '';
                }
                return;
            }

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelection(currentIndex < items.length - 1 ? currentIndex + 1 : 0);
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    setSelection(currentIndex > 0 ? currentIndex - 1 : items.length - 1);
                    break;

                case 'Enter':
                    if (currentIndex >= 0 && items[currentIndex]) {
                        e.preventDefault();
                        items[currentIndex].click();
                    } else {
                        const viewAllLink = searchDropdown.querySelector('[data-search-all-results="true"]');
                        if (viewAllLink) {
                            e.preventDefault();
                            viewAllLink.click();
                        }
                    }
                    break;

                case 'Escape':
                    e.preventDefault();
                    clearSelection();
                    searchDropdown.innerHTML = '';
                    searchInput.focus();
                    break;

                default:
                    clearSelection();
            }
        }

        searchInput.addEventListener('keydown', handleSearchInputKeydown);

        // Re-initialize items after HTMX swaps
        searchDropdown.addEventListener('htmx:afterSwap', () => {
            updateItems();
            clearSelection();
        });

        // Close dropdown on click outside
        document.addEventListener('click', (e) => {
            if (e.target.closest('.dropdown, .dropdown-menu')) {
                return;
            }

            if (
                !searchInput.contains(e.target) &&
                !searchDropdown.contains(e.target) &&
                !searchToggleBtn?.contains(e.target)
            ) {
                clearSelection();
            }
        });
    }
});

// ============================================================
// Entry Sequence — remove class after animation completes
// ============================================================
const entryEl = document.querySelector('.gt-entry-sequence');
if (entryEl) {
    const onAnimEnd = () => {
        entryEl.classList.remove('gt-entry-sequence');
        entryEl.removeEventListener('animationend', onAnimEnd);
    };
    entryEl.addEventListener('animationend', onAnimEnd);
}

// ============================================================
// Scroll-Reveal IntersectionObserver
// ============================================================
const revealObserver = new IntersectionObserver(
    (entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('reveal-visible');
                revealObserver.unobserve(entry.target);
            }
        });
    },
    { rootMargin: '0px 0px -60px 0px', threshold: 0.1 }
);

document
    .querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')
    .forEach((el) => revealObserver.observe(el));

// ============================================================
// Scroll-Aware Header
// ============================================================
let scrollRAF = false;
const onScroll = () => {
    if (scrollRAF) return;
    scrollRAF = true;
    requestAnimationFrame(() => {
        document.body.classList.toggle('header-scrolled', window.scrollY > 80);
        scrollRAF = false;
    });
};
window.addEventListener('scroll', onScroll, { passive: true });
if (window.scrollY > 80) document.body.classList.add('header-scrolled');

// ============================================================
// Magnetic Hover Effect
// ============================================================
document.querySelectorAll('.magnetic-wrap').forEach((el) => {
    el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        el.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
    });
    el.addEventListener('mouseleave', () => {
        el.style.transform = 'translate(0, 0)';
    });
});

/* Project specific Javascript goes here. */
export { loadAsyncThumbnails };
